import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ExportToCsv } from 'export-to-csv';
import * as moment from 'moment';
import { forkJoin } from 'rxjs';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { Campagne } from 'src/app/data/declaration/models/campagne.model';
import { DeclarationExport } from 'src/app/data/declaration/models/declaration-export.model';
import { RefMouvement } from 'src/app/data/declaration/models/ref-mouvement.model';
import { DeclarationsService } from 'src/app/data/declaration/services/declarations/declarations.service';
import { ReferentielService } from 'src/app/data/declaration/services/referentiel/referentiel.service';
import { Cahier } from 'src/app/data/habilitation/models/cahier.model';
import { CahiersService } from 'src/app/data/habilitation/services/cahiers/cahiers.service';
import { OperateursService } from 'src/app/data/intervenant/services/operateurs/operateurs.service';
import { atLeastOneRequiredValidator } from './exporter-declarations/exporter-declarations.component';
import { DeclarationExportRequest } from '../../../../data/declaration/models/declaration-export-request.model';
import { LoaderService } from '../../../../core/services/loader/loader.service';

@Component({
    selector: 'app-generation-declarations',
    templateUrl: './generation-declarations.component.html',
    styleUrls: ['./generation-declarations.component.scss'],
    standalone: false
})
export class GenerationDeclarationsComponent implements OnInit {
    public refAnnees: Campagne[] = [];
    public refCahiers: Cahier[];
    public refMouvements: RefMouvement[];
    public formDeclaration: UntypedFormGroup;

    public exportDeclarations: DeclarationExport[];

    get cdcs() {
        return this.formDeclaration.get('cdcs');
    }

    get mvts() {
        return this.formDeclaration.get('mvts');
    }

    get annees() {
        return this.formDeclaration.get('annees');
    }

    get dateRangeError(): boolean {
        return this.formDeclaration.hasError('invalidDateRange') && this.formDeclaration.get('dateMin').value && this.formDeclaration.get('dateMax').value;
    }

    /**
     * True if a result of export is available.
     */
    hasResult = false;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly cahiersService: CahiersService,
        private readonly declarationService: DeclarationsService,
        private readonly loaderService: LoaderService,
        private readonly referentielService: ReferentielService,
        private readonly operateursService: OperateursService,
        private readonly route: ActivatedRoute,
        private readonly router: Router
    ) {}

    ngOnInit() {
        this.loadData();
    }

    /**
     * Charge la pression dans le cas d'une modification et charge toutes les données
     * nécessaires nécessaires au remplissage des listes déroulantes
     */
    private loadData(): void {
        forkJoin(this.cahiersService.getCahiers(), this.referentielService.getReferentiel())
            .pipe(this.loaderService.operator())
            .subscribe(([cahiers, referentiel]) => {
                this.refAnnees = referentiel.campagnes.sort((a, b) => {
                    return b.annee - a.annee;
                });
                this.refCahiers = cahiers;
                this.refMouvements = referentiel.refMouvements;
                this.initForm();
            });
    }

    /** Initialise le formulaire */
    private initForm(): void {
        this.formDeclaration = this.fb.group(
            {
                cdcs: [undefined],
                mvts: [undefined],
                annees: [undefined],
                dateMin: [null],
                dateMax: [null]
            },
            { validators: [this.dateRangeValidator, atLeastOneRequiredValidator] }
        );
    }

    public onSubmit(): void {
        this.hasResult = false;
        if (this.formDeclaration.valid) {
            const request = new DeclarationExportRequest();
            const dateMin = this.formDeclaration.get('dateMin').value;
            const dateMax = this.formDeclaration.get('dateMax').value;
            if (this.annees.value) {
                this.annees.value.forEach(element => {
                    request.idCampagnes.push(element.id);
                });
            } else {
                this.refAnnees.forEach(element => {
                    request.idCampagnes.push(element.id);
                });
            }

            if (this.mvts.value) {
                this.mvts.value.forEach(element => {
                    request.idRefMouvements.push(element.id);
                });
            } else {
                this.refMouvements.forEach(element => {
                    request.idRefMouvements.push(element.id);
                });
            }

            if (this.cdcs.value && this.cdcs.value.length > 0) {
                this.cdcs.value.forEach(element => {
                    request.idCahiers.push(element.id);
                });
            } else {
                this.refCahiers.forEach(element => {
                    request.idCahiers.push(element.id);
                });
            }

            request.dateMin = dateMin ? `${dateMin.year}-${String(dateMin.month).padStart(2, '0')}-${String(dateMin.day).padStart(2, '0')}` : null;
            request.dateMax = dateMax ? `${dateMax.year}-${String(dateMax.month).padStart(2, '0')}-${String(dateMax.day).padStart(2, '0')}` : null;

            forkJoin([this.declarationService.getDeclarationExport(request)])
                .pipe(this.loaderService.operator())
                .subscribe(
                    ([declarations]) => {
                        this.hasResult = true;
                        this.exportDeclarations = declarations;

                        const idIntervenants = Array.from(
                            new Set(
                                this.exportDeclarations
                                    .filter(d => d.idIntervenant) // garde ceux qui ont un idIntervenant défini
                                    .map(d => d.idIntervenant) // extrait les idIntervenant
                            )
                        );

                        const idIntervenantAcheteurs = Array.from(
                            new Set(
                                this.exportDeclarations
                                    .filter(d => d.idIntervenantAcheteurTransaction) // garde ceux qui ont un idIntervenant défini
                                    .map(d => d.idIntervenantAcheteurTransaction) // extrait les idIntervenant
                            )
                        );

                        this.operateursService
                            .getOperateursById(SessionContext.get('idOrganisme'), idIntervenants.concat(idIntervenantAcheteurs))
                            .subscribe(operateurs => {
                                this.exportDeclarations.forEach(h => {
                                    const op = operateurs.find(o => o.id === h.idIntervenant);
                                    if (op) {
                                        h.adresse = op.adresse;
                                        h.codePostal = op.codePostal;
                                        h.ville = op.ville;
                                    }

                                    const opAch = operateurs.find(o => o.id === h.idIntervenantAcheteurTransaction);
                                    if (h.mouvement === 'Transaction') {
                                        if (opAch) {
                                            h.raisonSocialeAcheteurTransaction = opAch.raisonSociale;
                                            h.siretAcheteurTransaction = opAch.siret;
                                            h.typeTransaction = opAch.pays == null || opAch.pays.code === 'FRANCE' ? 'FRANCE' : 'EXPORT';
                                        }
                                    }
                                });

                                this.exporterDeclarations();
                            });
                    },
                    err => {
                        console.error(err);
                        alert("Erreur lors de l'export.");
                    }
                );
        } else {
            this.markAllControlsTouched(this.formDeclaration);
            return;
        }
    }

    markAllControlsTouched(control: AbstractControl) {
        if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
            Object.values(control.controls).forEach(innerControl => this.markAllControlsTouched(innerControl));
        } else {
            control.markAsTouched();
        }
    }

    private dateRangeValidator(form: UntypedFormGroup): { [key: string]: any } | null {
        const dateMin = form.get('dateMin').value;
        const dateMax = form.get('dateMax').value;

        if (dateMin && dateMax) {
            const isRangeValid =
                dateMin.year < dateMax.year ||
                (dateMin.year === dateMax.year && dateMin.month < dateMax.month) ||
                (dateMin.year === dateMax.year && dateMin.month === dateMax.month && dateMin.day <= dateMax.day);

            return isRangeValid ? null : { invalidDateRange: true };
        }

        return null;
    }

    cleanText(texte) {
        if (!texte) {
            return '';
        }
        return texte
            .replace(/;|\r?\n/g, match => (match === ';' ? '-' : ' ')) // ; → -, retour à la ligne → espace
            .replace(/\s+/g, ' ') // supprime espaces multiples
            .trim(); // retire espace en début/fin
    }

    exporterDeclarations(): void {
        try {
            if (this.exportDeclarations.length === 0) {
                return;
            }

            const data = [];
            this.exportDeclarations.forEach(exp => {
                data.push({
                    campagne: exp.campagne,
                    raisonSociale: exp.raisonSociale,
                    adresse: exp.adresse != null ? this.cleanText(exp.adresse) : '',
                    codePostal: exp.codePostal != null ? exp.codePostal : '',
                    ville: exp.ville != null ? exp.ville : '',
                    numeroCVI: exp.numeroCVI != null ? exp.numeroCVI : '',
                    numeroDossier: exp.numeroDossier != null ? exp.numeroDossier : '',
                    dateMvt: exp.dateMvt ? exp.dateMvt.format('DD/MM/YYYY') : '',
                    logement: this.cleanText(exp.logement),
                    numero: this.cleanText(exp.numero),
                    cahierDesCharges: exp.cahierDesCharges,
                    produit: exp.produit,
                    couleur: exp.couleur,
                    mouvement: exp.mouvement,
                    volume: exp.volume != null ? +exp.volume.toFixed(2) : '',
                    cepage: exp.cepage != null ? exp.cepage : '',
                    destination: exp.destination != null ? exp.destination : '',
                    siteEntreposage: this.cleanText(exp.siteEntreposage),
                    evenement: exp.evenement != null ? exp.evenement : '',
                    dateEvenement: exp.dateEvenement ? exp.dateEvenement.format('DD/MM/YYYY') : '',
                    typeLotChangementProduit: exp.typeLotChangementProduit != null ? exp.typeLotChangementProduit : '',
                    cdcOrigineChangementProduit: exp.cdcOrigineChangementProduit != null ? exp.cdcOrigineChangementProduit : '',
                    produitOrigineChangementProduit: exp.produitOrigineChangementProduit != null ? exp.produitOrigineChangementProduit : '',
                    typeTransaction: exp.typeTransaction != null ? exp.typeTransaction : '',
                    raisonSocialeAcheteurTransaction: exp.raisonSocialeAcheteurTransaction != null ? exp.raisonSocialeAcheteurTransaction : '',
                    siretAcheteurTransaction: exp.siretAcheteurTransaction != null ? exp.siretAcheteurTransaction : '',
                    numContrat: exp.numContrat != null ? exp.numContrat : '',
                    numTirage: exp.numTirage != null ? exp.numTirage : '',
                    typeVinTirage: exp.typeVinTirage != null ? exp.typeVinTirage : '',
                    volumeVinBase: exp.volumeVinBase != null ? +exp.volumeVinBase.toFixed(2) : '',
                    raisonSocialePrestataire: exp.raisonSocialePrestataire != null ? exp.raisonSocialePrestataire : '',
                    siretPrestataire: exp.siretPrestataire != null ? exp.siretPrestataire : '',
                    mentionValorisante: exp.mentionValorisante != null ? exp.mentionValorisante : '',
                    surfaceLot: exp.surfaceLot != null ? +exp.surfaceLot.toFixed(2) : '',
                    volumeVinRevendique: exp.volumeVinRevendique != null ? +exp.volumeVinRevendique.toFixed(2) : '',
                    vciN1Revendique: exp.vciN1Revendique != null ? +exp.vciN1Revendique.toFixed(2) : '',
                    vciN1raffraichi: exp.vciN1raffraichi != null ? +exp.vciN1raffraichi.toFixed(2) : '',
                    vciN1complement: exp.vciN1complement != null ? +exp.vciN1complement.toFixed(2) : '',
                    vciN1substitution: exp.vciN1substitution != null ? +exp.vciN1substitution.toFixed(2) : '',
                    vciDetruit: exp.vciDetruit != null ? +exp.vciDetruit.toFixed(2) : '',
                    vciNouveau: exp.vciNouveau != null ? +exp.vciNouveau.toFixed(2) : '',
                    stockVci: exp.stockVci != null ? +exp.stockVci.toFixed(2) : '',
                    type: exp.typeRevendication || '',
                    environnementLabels: exp.labelsEnvironnementaux || ''
                });
            });

            // Si, pour une raison quelconque, la transformation produit un tableau vide,
            // on évite d’appeler la librairie qui planterait.
            if (data.length === 0) {
                alert('Aucune donnée à exporter après transformation.');
                return;
            }

            const options = {
                filename: 'EXPORT_DECLARATION'.concat('_').concat(moment().format('YYYYMMDD')),
                fieldSeparator: ';',
                quoteStrings: '',
                decimalSeparator: ',',
                showLabels: true,
                showTitle: false,
                title: 'EXPORT_DECLARATION'.concat('_').concat(moment().format('YYYYMMDD')),
                useTextFile: false,
                useBom: true,
                useKeysAsHeaders: false,
                headers: [
                    'Millésime',
                    'Raison Sociale',
                    'Adresse',
                    'Code postal',
                    'Ville',
                    'N° CVI',
                    'N° Dossier',
                    'Date',
                    'Logement',
                    'N° Lot',
                    'Cahier des charges',
                    'Produit',
                    'Couleur',
                    'Déclaration (Type)',
                    'Volume (Hl)',
                    'Cépage(s)',
                    'Destination(s)',
                    'Site entreposage',
                    'Evènement',
                    'Date évènement',
                    'Changement de produit - Type de lot',
                    "Changement de produit - Cahier des charges d'origine",
                    "Changement de produit - Produit d'origine",
                    'Transaction - Type ',
                    'Transaction - Raison sociale acheteur',
                    'Transaction - SIRET acheteur',
                    'Transaction - N° de contrat',
                    'Tirage - N° de tirage',
                    'Tirage - Type de vin',
                    'Tirage - Volume vin de base (hl)',
                    'Tirage - Raison sociale prestataire',
                    'Tirage - SIRET prestataire',
                    'Mention valorisante',
                    'Surface (ha)',
                    'Volume de vins N revendiqués (hl)',
                    'VCI N-1 revendiqué (hl)',
                    'VCI N-1 rafraichi (hl)',
                    'VCI N-1 complément (hl)',
                    'VCI N-1 substitution (hl)',
                    'VCI détruit (hl)',
                    'VCI nouveau (hl)',
                    'Stock VCI N (hl)',
                    'Type de revendication',
                    'Labels environnementaux'
                ]
            };

            const csvExporter = new ExportToCsv(options);

            csvExporter.generateCsv(data);
        } catch (error) {
            console.error("Erreur pendant l'export CSV :", error);
            alert("Une erreur est survenue pendant l'export. Veuillez réessayer.");
        } finally {
            //
        }
    }
}
